/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ExecutionScheduledLocation } from './ExecutionScheduledLocation';
import {
    ExecutionScheduledLocationFromJSON,
    ExecutionScheduledLocationFromJSONTyped,
    ExecutionScheduledLocationToJSON,
    ExecutionScheduledLocationToJSONTyped,
} from './ExecutionScheduledLocation';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { ServiceLevelAgreement } from './ServiceLevelAgreement';
import {
    ServiceLevelAgreementFromJSON,
    ServiceLevelAgreementFromJSONTyped,
    ServiceLevelAgreementToJSON,
    ServiceLevelAgreementToJSONTyped,
} from './ServiceLevelAgreement';
import type { Operation } from './Operation';
import {
    OperationFromJSON,
    OperationFromJSONTyped,
    OperationToJSON,
    OperationToJSONTyped,
} from './Operation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';
import type { Location } from './Location';
import {
    LocationFromJSON,
    LocationFromJSONTyped,
    LocationToJSON,
    LocationToJSONTyped,
} from './Location';

/**
 * Response schema for Service logistics object.
 * @export
 * @interface ServiceResponse
 */
export interface ServiceResponse {
    /**
     * The executionStatus indicates the current status of the service execution.
     *                                      
     * @type {string}
     * @memberof ServiceResponse
     */
    executionStatus?: string;
    /**
     * The invoice status allows to know if a service has been invoiced.
     *                       List of values:
     *                       NOT_INVOICED: not yet invoiced
     *                       PARTIALLY_INVOICED: at least one invoice has been issued but it remains an amount to be invoiced
     *                       INVOICED: the invoicing process is supposed to be finished for this service.
     *                       Even if there is no amount to be invoiced, meaning no invoice will be issued, the status should move to "INVOICED" to indicate the invoicing process is finished.
     * @type {string}
     * @memberof ServiceResponse
     */
    invoiceStatus?: string;
    /**
     * Indicates if the service has to be considered dangerous. The fact a service is related to dangerous goods is not the only trigger to decide the service is dangerous. The information is coming from the requestor of the service and applied by the provider as a condition to execute it.
     * @type {boolean}
     * @memberof ServiceResponse
     */
    isDangerous?: boolean;
    /**
     * Free text to describe the product when there is no information about BatchOfGoods or pieces and items.
     * @type {string}
     * @memberof ServiceResponse
     */
    productDescription?: string;
    /**
     * refers to the contractual terms and conditions described in a global contract defined for a period and not for each individual service rendered.
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceAgreementReference?: string;
    /**
     * A description of the service rendered. Free text.
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceDescription?: string;
    /**
     * Free text name given to the service for requestor and provider to ease its recognition.
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceName?: string;
    /**
     * Service Nature is an enum of all the possible natures of services.
     *                                       List of possible values described in ENUM ontology....TBD
     *                                       Examples of servicesNatures:
     *                                         - LOGISTICS
     *                                         - FINANCE
     *                                         - ADMIN
     *                                         - IT
     *                                         ...
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceNature?: string;
    /**
     * Service status represent the status of the contractual agreement between the requestor and the provider. It is not related to the execution which is described in the executionStatus.
     * List of possible values:
     * - DRAFT: the provider is currently writing a proposed service
     * - PROPOSED: the provider has proposed a service to the requestor
     * - ACCEPTED: the requestor has accepted the service proposed by the provider
     * - CANCELLED: both parties have accepted to cancel the service before or after starting the execution.
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceStatus?: string;
    /**
     * The serviceType can be completed by a serviceSubtype to better describe the service.
     *                        For the serviceType ROAD_TRANSPORT, the following service Subtypes complete the description:
     *                        - FTL_LTL for Road full truck load or less than truck load
     *                        - MILKUN
     *                        - EXPRESS
     *                        - COURRIER
     *                        ...
     *                        
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceSubtype?: string;
    /**
     * serviceType is an enum of all the possible types of services.
     *                     Each type of service can be redefined in a subclass to overwrite the master class Service.
     *                     List of possible values described in ENUM ontology...
     * @type {string}
     * @memberof ServiceResponse
     */
    serviceType?: string;
    /**
     * Scheduled location where the service is starting.
     *                     It can be the same location as the location of startLocation or executionLocation of the first activity but not always.
     *                     Service Start location and first activity location would be different when an activity has to be performed before starting the execution of the service itself.
     *                     It contains also the requested, scheduled and actual calendar at the start location
     * @type {ExecutionScheduledLocation}
     * @memberof ServiceResponse
     */
    startLocation?: ExecutionScheduledLocation;
    /**
     * Scheduled location where the service is ending.
     *                     It can be the same location as the location of endLocation or executionLocation of the last activity but not always.
     *                     Service end location and last activity location would be different when an activity has to be performed after the service is rendered.
     *                     It contains also the requested, scheduled and actual calendar at the end location
     * @type {ExecutionScheduledLocation}
     * @memberof ServiceResponse
     */
    endLocation?: ExecutionScheduledLocation;
    /**
     * The list of activities represents the process tasks scheduled to perform a service. Not all activities have to be detailed; only those related to a schedule are described. For example, if loading a truck must be scheduled independently from transport, there will be two activities: one for loading and another for transport. However, it may not be relevant to identify the loading separately. In that case, only the transport activity is identified, assuming that loading is included in the transport. The level of detail required in the execution process of the service determines the extent to which activities are described.
     * @type {Array<Link>}
     * @memberof ServiceResponse
     */
    scheduledActivities?: Array<Link>;
    /**
     * List of consignments served by a service. At least, the first service in the chain contains the consignments. The service executing part of the logistic chain may know the consignments in the chain is needed. If not needed, the consignment itself is not known by the service provider who would only know the actions related to the pieces/resources and the special characteristics like dangerous goods.
     * @type {Array<Link>}
     * @memberof ServiceResponse
     */
    consignments?: Array<Link>;
    /**
     * List of contacts related to the service. This can include persons or teams to be contacted about the service.
     * @type {Array<Contact>}
     * @memberof ServiceResponse
     */
    contacts?: Array<Contact>;
    /**
     * List of documents used for the service, such as contractual documents like CMR, waybill, insurance declaration, etc.
     * @type {Array<Document>}
     * @memberof ServiceResponse
     */
    documents?: Array<Document>;
    /**
     * External references of the service can be used when they are defined in a legacy system that registers the service under a different reference identifier than the URI. This can be useful for searching the service using the ID from the legacy app. Frequent ExternalReference Types:
     *                            - 'CUSTOMER_ID'
     *                            - 'PROVIDER_ID'
     * @type {Array<ExternalReference>}
     * @memberof ServiceResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * The final location of the goods where they are delivered to. It is a key information for calculating the taxes of the service when the transport is not delivering to the final destination of the logistic chain.
     * @type {Location}
     * @memberof ServiceResponse
     */
    finalLocation?: Location;
    /**
     * 
     * @type {Location}
     * @memberof ServiceResponse
     */
    originLocation?: Location;
    /**
     * Service Requests from the customers.
     * @type {Array<Link>}
     * @memberof ServiceResponse
     */
    serviceRequests?: Array<Link>;
    /**
     * The provider is the party accountable for the execution of the service and is the owner of the service.
     * @type {Party}
     * @memberof ServiceResponse
     */
    provider?: Party;
    /**
     * 
     * @type {Party}
     * @memberof ServiceResponse
     */
    requestor?: Party;
    /**
     * The sales operations refer to the unit operations to be invoiced in the service. These operations can be identified upfront when the service is requested or added later.
     * @type {Array<Operation>}
     * @memberof ServiceResponse
     */
    saleOperations?: Array<Operation>;
    /**
     * SLA stands for Service Level Agreement. It is an agreement between the customer and the provider that defines the level of service to be provided as part of their contractual relationship. Although there is no need to standardize this B2B value, it is important for both parties to clearly define the service levels and expectations to avoid any misunderstandings or disputes.
     * @type {ServiceLevelAgreement}
     * @memberof ServiceResponse
     */
    sla?: ServiceLevelAgreement;
    /**
     * Special characteristics applied to the service and to be shared between provider and customer.
     * @type {Array<SpecialCharacteristic>}
     * @memberof ServiceResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the service and to be shared with a provider, such as handling instructions or special protocol to apply.
     * @type {Array<SpecialInstruction>}
     * @memberof ServiceResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * The total amount is the sum of the amounts calculated for each individual operation, regardless of their invoicing status. The amount should be calculated in the main invoicing currency.
     * @type {Value}
     * @memberof ServiceResponse
     */
    totalOperationAmount?: Value;
    /**
     * Amount declared for insurance or customs declaration purpose.
     * @type {Value}
     * @memberof ServiceResponse
     */
    declaredValue?: Value;
    /**
     * The invoiced party is the party invoiced for the service.
     * @type {Party}
     * @memberof ServiceResponse
     */
    invoicedParty?: Party;
    /**
     * The Intercom (International Commercial Terms) is a 3 digits codes defining the the contractual terms between the seller and the buyer regarding the delivery of the goods and the payment of the taxes.
     *                  The International Chamber of Commerce (ICC) provides the list of the codes.
     * @type {string}
     * @memberof ServiceResponse
     */
    incoterm?: string;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof ServiceResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the ServiceResponse interface.
 */
export function instanceOfServiceResponse(value: object): value is ServiceResponse {
    return true;
}

export function ServiceResponseFromJSON(json: any): ServiceResponse {
    return ServiceResponseFromJSONTyped(json, false);
}

export function ServiceResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): ServiceResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'executionStatus': json['executionStatus'] == null ? undefined : json['executionStatus'],
        'invoiceStatus': json['invoiceStatus'] == null ? undefined : json['invoiceStatus'],
        'isDangerous': json['isDangerous'] == null ? undefined : json['isDangerous'],
        'productDescription': json['productDescription'] == null ? undefined : json['productDescription'],
        'serviceAgreementReference': json['serviceAgreementReference'] == null ? undefined : json['serviceAgreementReference'],
        'serviceDescription': json['serviceDescription'] == null ? undefined : json['serviceDescription'],
        'serviceName': json['serviceName'] == null ? undefined : json['serviceName'],
        'serviceNature': json['serviceNature'] == null ? undefined : json['serviceNature'],
        'serviceStatus': json['serviceStatus'] == null ? undefined : json['serviceStatus'],
        'serviceSubtype': json['serviceSubtype'] == null ? undefined : json['serviceSubtype'],
        'serviceType': json['serviceType'] == null ? undefined : json['serviceType'],
        'startLocation': json['startLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['endLocation']),
        'scheduledActivities': json['scheduledActivities'] == null ? undefined : ((json['scheduledActivities'] as Array<any>).map(LinkFromJSON)),
        'consignments': json['consignments'] == null ? undefined : ((json['consignments'] as Array<any>).map(LinkFromJSON)),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'finalLocation': json['finalLocation'] == null ? undefined : LocationFromJSON(json['finalLocation']),
        'originLocation': json['originLocation'] == null ? undefined : LocationFromJSON(json['originLocation']),
        'serviceRequests': json['serviceRequests'] == null ? undefined : ((json['serviceRequests'] as Array<any>).map(LinkFromJSON)),
        'provider': json['provider'] == null ? undefined : PartyFromJSON(json['provider']),
        'requestor': json['requestor'] == null ? undefined : PartyFromJSON(json['requestor']),
        'saleOperations': json['saleOperations'] == null ? undefined : ((json['saleOperations'] as Array<any>).map(OperationFromJSON)),
        'sla': json['sla'] == null ? undefined : ServiceLevelAgreementFromJSON(json['sla']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'totalOperationAmount': json['totalOperationAmount'] == null ? undefined : ValueFromJSON(json['totalOperationAmount']),
        'declaredValue': json['declaredValue'] == null ? undefined : ValueFromJSON(json['declaredValue']),
        'invoicedParty': json['invoicedParty'] == null ? undefined : PartyFromJSON(json['invoicedParty']),
        'incoterm': json['incoterm'] == null ? undefined : json['incoterm'],
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function ServiceResponseToJSON(json: any): ServiceResponse {
    return ServiceResponseToJSONTyped(json, false);
}

export function ServiceResponseToJSONTyped(value?: ServiceResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'executionStatus': value['executionStatus'],
        'invoiceStatus': value['invoiceStatus'],
        'isDangerous': value['isDangerous'],
        'productDescription': value['productDescription'],
        'serviceAgreementReference': value['serviceAgreementReference'],
        'serviceDescription': value['serviceDescription'],
        'serviceName': value['serviceName'],
        'serviceNature': value['serviceNature'],
        'serviceStatus': value['serviceStatus'],
        'serviceSubtype': value['serviceSubtype'],
        'serviceType': value['serviceType'],
        'startLocation': ExecutionScheduledLocationToJSON(value['startLocation']),
        'endLocation': ExecutionScheduledLocationToJSON(value['endLocation']),
        'scheduledActivities': value['scheduledActivities'] == null ? undefined : ((value['scheduledActivities'] as Array<any>).map(LinkToJSON)),
        'consignments': value['consignments'] == null ? undefined : ((value['consignments'] as Array<any>).map(LinkToJSON)),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'finalLocation': LocationToJSON(value['finalLocation']),
        'originLocation': LocationToJSON(value['originLocation']),
        'serviceRequests': value['serviceRequests'] == null ? undefined : ((value['serviceRequests'] as Array<any>).map(LinkToJSON)),
        'provider': PartyToJSON(value['provider']),
        'requestor': PartyToJSON(value['requestor']),
        'saleOperations': value['saleOperations'] == null ? undefined : ((value['saleOperations'] as Array<any>).map(OperationToJSON)),
        'sla': ServiceLevelAgreementToJSON(value['sla']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'totalOperationAmount': ValueToJSON(value['totalOperationAmount']),
        'declaredValue': ValueToJSON(value['declaredValue']),
        'invoicedParty': PartyToJSON(value['invoicedParty']),
        'incoterm': value['incoterm'],
        'id': value['id'],
    };
}

